import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/AuthContext";
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import {
  addMonths, buildMonthGrid, formatRangeLabel, isSameDay, monthLabel, startOfDay,
} from "../../lib/time";
import { cn } from "../../lib/utils";

export interface DateRangeValue { start: Date; end: Date; preset: string | null }

const PRESETS: { id: string; label: string; resolve: () => { start: Date; end: Date } }[] = [
  { id: "last_7",  label: "Last 7 Days",  resolve: () => ({ start: startOfDay(new Date(Date.now() - 6 * 864e5)),  end: startOfDay(new Date()) }) },
  { id: "last_30", label: "Last 30 Days", resolve: () => ({ start: startOfDay(new Date(Date.now() - 29 * 864e5)), end: startOfDay(new Date()) }) },
  {
    id: "quarter", label: "This Quarter", resolve: () => {
      const n = new Date();
      return { start: new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1), end: startOfDay(n) };
    },
  },
];

export function presetRange(id: string): { start: Date; end: Date } {
  return (PRESETS.find((p) => p.id === id) ?? PRESETS[0]).resolve();
}

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

export function DateRangePicker({ value, onChange, align = "left", triggerClassName, dropUp: explicitDropUp, timeZone }: {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  align?: "left" | "right";
  triggerClassName?: string;
  dropUp?: boolean;
  timeZone?: string | null;
}) {
  const { user } = useAuth() as any;
  const activeTz = timeZone || user?.timezone;
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => new Date(value.start.getFullYear(), value.start.getMonth(), 1));
  const [draftStart, setDraftStart] = useState<Date | null>(value.preset === null ? value.start : null);
  const [draftEnd, setDraftEnd] = useState<Date | null>(value.preset === null ? value.end : null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});

  const today = startOfDay(new Date());

  const PANEL_W = 560;
  const PANEL_H = 420;

  // Position the panel with fixed viewport coordinates (recomputed on open,
  // scroll, and resize) so it can never be clipped by a scrollable ancestor —
  // e.g. the CRM Leads/Customers filter popover, which scrolls internally.
  // Same approach as the single-date DatePicker's portaled menu.
  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    const openUp = explicitDropUp ?? (spaceBelow < PANEL_H && spaceAbove > spaceBelow);

    let left = align === "right" ? r.right - PANEL_W : r.left;
    if (left + PANEL_W > window.innerWidth - 8) left = window.innerWidth - PANEL_W - 8;
    if (left < 8) left = 8;

    setPopoverStyle({
      position: "fixed",
      left: `${left}px`,
      width: `${PANEL_W}px`,
      ...(openUp
        ? { bottom: `${window.innerHeight - r.top + 8}px` }
        : { top: `${r.bottom + 8}px` }),
      zIndex: 9999,
    });
  }, [align, explicitDropUp]);

  useEffect(() => {
    if (!open) return;
    place();
    const handleScrollOrResize = () => place();
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", handlePointerDown, true);
    return () => window.removeEventListener("mousedown", handlePointerDown, true);
  }, [open]);

  const handleToggle = () => {
    if (!open) place();
    setOpen((o) => !o);
  };

  const pickDay = (d: Date) => {
    if (!draftStart || (draftStart && draftEnd)) { setDraftStart(d); setDraftEnd(null); return; }
    if (d < draftStart) { setDraftStart(d); return; }
    setDraftEnd(d);
  };

  const apply = () => {
    if (draftStart && draftEnd) {
      onChange({ start: draftStart, end: draftEnd, preset: null });
      setOpen(false);
    }
  };

  const label = value.preset
    ? PRESETS.find((p) => p.id === value.preset)?.label ?? "Custom"
    : formatRangeLabel(value.start, value.end, activeTz);

  return (
    <div className="relative" ref={triggerRef}>
      {/* Trigger */}
      <button
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          "flex items-center gap-1.5 rounded-lg border border-line bg-elevated px-2.5 py-1 text-xs font-medium text-ink transition-colors hover:bg-soft h-8",
          triggerClassName
        )}
      >
        <Calendar className="h-3.5 w-3.5 text-ink-3" />
        <span>{label}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>

      {/* Portaled popover (renders in document.body via fixed coordinates,
          so it's never clipped by a scrollable ancestor like the filter panel) */}
      {open && createPortal(
        <div
          ref={popoverRef}
          style={popoverStyle}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex overflow-hidden rounded-2xl border border-line bg-elevated shadow-2xl"
        >
          {/* Presets */}
          <div className="w-40 shrink-0 border-r border-line bg-soft/50 p-2">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => { onChange({ ...p.resolve(), preset: p.id }); setOpen(false); }}
                className={cn(
                  "block w-full rounded-lg px-3 py-2 text-left text-sm transition-colors",
                  value.preset === p.id ? "bg-brand-soft font-semibold text-brand" : "text-ink-2 hover:bg-soft",
                )}
              >
                {p.label}
              </button>
            ))}
            <p className={cn("mt-2 rounded-lg px-3 py-2 text-xs", value.preset === null ? "font-semibold text-brand" : "text-ink-3")}>
              {value.preset === null ? "Custom range active" : "Or pick dates →"}
            </p>
          </div>

          {/* Dual-month calendar */}
          <div className="flex-1 p-4">
            <div className="mb-3 flex items-center justify-between">
              <button onClick={() => setViewMonth((m) => addMonths(m, -1))} aria-label="Previous month"
                className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="font-mono text-xs font-semibold text-ink">
                {monthLabel(viewMonth)} · {monthLabel(addMonths(viewMonth, 1))}
              </span>
              <button onClick={() => setViewMonth((m) => addMonths(m, 1))} aria-label="Next month"
                className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[viewMonth, addMonths(viewMonth, 1)].map((month, mi) => (
                <div key={mi}>
                  <p className="mb-1 text-center text-xs font-semibold text-ink-2">{monthLabel(month, activeTz)}</p>
                  <div className="grid grid-cols-7">
                    {WEEKDAYS.map((w, i) => (
                      <span key={i} className="py-1 text-center font-mono text-[10px] text-ink-3">{w}</span>
                    ))}
                    {buildMonthGrid(month).map((d, i) =>
                      d === null ? (
                        <span key={`b${i}`} />
                      ) : (
                        <button
                          key={d.toISOString()}
                          disabled={d > today}
                          onClick={() => pickDay(d)}
                          aria-label={d.toDateString()}
                          className={cn(
                            "mx-auto my-0.5 flex h-7 w-7 items-center justify-center rounded-lg text-xs transition-colors",
                            d > today && "cursor-not-allowed text-line",
                            d <= today && "text-ink-2 hover:bg-soft",
                            isSameDay(d, today) && "border border-line",
                            (draftStart && isSameDay(d, draftStart)) || (draftEnd && isSameDay(d, draftEnd))
                              ? "bg-brand font-bold text-white hover:bg-brand"
                              : draftStart && draftEnd && d > draftStart && d < draftEnd
                                ? "bg-brand-soft text-brand hover:bg-brand-soft"
                                : "",
                          )}
                        >
                          {d.getDate()}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
              <span className="font-mono text-xs text-ink-3">
                {draftStart ? formatRangeLabel(draftStart, draftEnd ?? draftStart, activeTz) : "Select a start date"}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setOpen(false)}
                  className="rounded-lg border border-line px-3 py-1.5 text-xs font-semibold text-ink hover:bg-soft">
                  Cancel
                </button>
                <button
                  onClick={apply}
                  disabled={!draftStart || !draftEnd}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-xs font-semibold text-white",
                    draftStart && draftEnd ? "bg-brand hover:bg-brand-hover" : "cursor-not-allowed bg-line text-ink-3",
                  )}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
